import React, { useState } from "react";
import { initials, tilePalette } from "../../lib/format";

/**
 * A mark's graphic representation, falling back to a monogram tile.
 *
 * The fallback is the normal case, not an error path: a pure word mark has
 * no figurative element to show, and the EUIPO image proxy answers 204 for
 * those. Showing a broken-image icon there would be wrong.
 */
const SIZES = {
  sm: "h-9 w-9 text-[10px]",
  table: "h-12 w-12 text-xs",
  lg: "h-20 w-20 text-lg",
  // Fills its container: the picture area at the top of a grid card.
  card: "h-36 w-full text-3xl",
};

export default function MarkThumbnail({
  wordMark,
  imageUrl,
  size = "table",
  onClick,
}) {
  // Remember *which* URL failed, not just that one did. The detail modal
  // reuses this component as you step to the next mark, and a plain boolean
  // would carry one mark's broken image over to the next mark's good one.
  const [failedUrl, setFailedUrl] = useState(null);
  const dims = SIZES[size] || SIZES.table;
  const showImage = imageUrl && failedUrl !== imageUrl;
  const clickable = showImage && Boolean(onClick);

  if (showImage) {
    return (
      <button
        type="button"
        onClick={clickable ? onClick : undefined}
        disabled={!clickable}
        title={clickable ? "View full size" : undefined}
        className={`${dims} flex shrink-0 items-center justify-center overflow-hidden rounded border border-ink-200 bg-white ${
          clickable
            ? "cursor-zoom-in hover:border-[#8f5eeb]-400"
            : "cursor-default"
        }`}
      >
        <img
          src={imageUrl}
          alt={wordMark ? `Logo for ${wordMark}` : "Trade mark logo"}
          className="max-h-full max-w-full object-contain"
          loading="lazy"
          onError={() => setFailedUrl(imageUrl)}
        />
      </button>
    );
  }

  const palette = tilePalette(wordMark);
  return (
    <span
      className={`${dims} flex shrink-0 items-center justify-center rounded font-display font-semibold`}
      style={{ backgroundColor: palette.bg, color: palette.fg }}
      title={wordMark || undefined}
      aria-hidden="true"
    >
      {initials(wordMark)}
    </span>
  );
}
